<template>
  <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img class="mx-auto h-[100px] w-auto" src="/img/spike-logo.svg" alt="Spike" />
      <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">Set your new password</h2>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div
        class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10"
        :class="result === 'exception' || result === 'GenericResult.Fail' ? 'shakeX' : ''"
      >
        <div class="space-y-6 text-gray-700 text-sm">
          <p> Please enter a new password. You will use this password next time you need to log in to Spike. </p>
          <p>
            You must use a strong password: at least {{ minLength }} characters, with an upper case letter, a lower case
            letter, and a number.
          </p>
          <div>
            <input
              v-model="password"
              type="password"
              autocomplete="off"
              required
              placeholder="password"
              class="appearance-none block w-full px-3 py-2 rounded-md border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary"
            />
            <PasswordMeter
              style="margin-top: -5px"
              :password="password"
              :min-length="minLength"
              @score="(s) => (valid = s == 4)"
            />
          </div>
          <div class="flex justify-center items-center">
            <button
              type="submit"
              class="w-24 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-600 disabled:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
              :disabled="result === 'loading' || result === 'GenericResult.Success' || !valid"
              @click="setPassword()"
            >
              <div v-if="result === 'loading'">Saving ...</div>
              <div v-else-if="result === 'GenericResult.Success'">Saved</div>
              <div v-else>Save</div>
            </button>
          </div>

          <div class="flex w-full justify-center text-sm">
            <template v-if="result === 'loading'">
              <ScalingDots />
            </template>
            <template v-else-if="result === 'exception'">
              <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
              <span class="ml-2 text-red-600">failed to contact the server, please retry</span>
            </template>
            <template v-else-if="result === 'GenericResult.Fail'">
              <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
              <span class="ml-2 text-red-600">error</span>
            </template>
            <template v-else-if="result === 'GenericResult.Success'">
              <CheckCircleIcon class="w-5 h-5 text-green-600" aria-hidden="true" />
              <span class="ml-2 text-green-600">success</span>
            </template>
            <template v-else>
              <!-- dummy - keep height -->
              <span>&nbsp;</span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Url, GenericResult, ScreenName } from "@spike/app2-sitemap/src/auth.js";
import axios from "axios";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/vue/solid";
import sleep from "@util/sleep";
import { onMounted, ref } from "vue";
import PasswordMeter from "../../components/password-meter.vue";
import ScalingDots from "../../components/spinners/ScalingDots.vue";
import * as Nav from "../../navigation";
import { Screen } from "../../routes";

const minLength = ref<number>(6);
const valid = ref<boolean>(false);
const password = ref<string | null>(null);
const result = ref<string | null>(null);

onMounted(async () => {
  // console.log("onMounted:", ScreenRoute.ForgotPasswordSetPassword);
  Nav.prevScreensComplete(ScreenName.ForgotPasswordSetPassword);
});

//#region setPassword

async function setPassword() {
  try {
    const requestBody = {
      password: password.value,
    };
    result.value = "loading";
    const response = await axios.post(Url.server.shared.forgotPassword.setPassword, requestBody);
    // console.log("set-password response:", response);
    const res = response.data;
    switch (res.result as GenericResult) {
      case GenericResult.Success:
        setPasswordSuccess();
        break;
      case GenericResult.Fail:
        setPasswordFail();
        break;
      default:
        setPasswordException();
        break;
    }
  } catch (e) {
    // console.error("set-password failed:", e);
    setPasswordException();
  }
}

async function setPasswordTest() {
  result.value = "loading";
  await sleep(500);

  // testing
  setPasswordSuccess();
  // setPasswordFail();
  // setPasswordException();
}

async function setPasswordSuccess() {
  result.value = "GenericResult.Success";
  await sleep(1000);
  Nav.complete(Screen.ForgotPasswordSetPassword);
  Nav.setActive(Screen.ForgotPasswordComplete, true);
}

async function setPasswordFail() {
  result.value = "GenericResult.Fail";
}

async function setPasswordException() {
  result.value = "exception";
}

//#endregion
</script>
